<script setup lang="ts">
import type { TTexts } from '@/types'
import Carousel from 'primevue/carousel'
import { onMounted, ref, watch } from 'vue'
import AES from 'crypto-js/aes'
import Utf8 from 'crypto-js/enc-utf8'

const props = defineProps<{ texts: TTexts; secret: string }>()
const autoplayInterval = 6000
const wsyText = ref<string>(props.texts.wsyde || '')
const imgs = ref<Array<{ src: string; alt: string }>>([])
const isLoading = ref(true)

onMounted(() => {
  setInterval(() => {
    // Pick one random text from texts where the key starts with 'wsy'
    const wsyKeys = Object.keys(props.texts).filter((key) => key.startsWith('wsy'))
    const randomKey = wsyKeys[Math.floor(Math.random() * wsyKeys.length)]
    wsyText.value = props.texts[randomKey as keyof TTexts] || ''
  }, autoplayInterval)
})

// Dynamically get all encrypted image files
const encryptedImageModules = import.meta.glob('../../imgs/encrypted/*.enc', {
  eager: true,
  query: '?url',
  import: 'default',
})

const encryptedImageFiles = Object.keys(encryptedImageModules).map((path) =>
  path.split('/').pop() || '',
)

async function decryptImage(encryptedText: string, password: string): Promise<string> {
  try {
    // Decrypt using crypto-js (same method as texts)
    const decryptedBase64 = AES.decrypt(encryptedText, password).toString(Utf8)

    if (!decryptedBase64) {
      throw new Error('Decryption failed - empty result')
    }

    // Convert base64 to blob
    const binaryString = atob(decryptedBase64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }

    // Create object URL from blob
    const blob = new Blob([bytes], { type: 'image/jpeg' })
    return URL.createObjectURL(blob)
  } catch (error) {
    console.error('Decryption failed:', error)
    throw error
  }
}

async function loadImages() {
  // In dev mode, load unencrypted images directly
  // if (import.meta.env.DEV) {
  //   const imageModules = import.meta.glob('../../imgs/cropped/*.{jpg,jpeg,png,gif,bmp}', {
  //     eager: true,
  //     query: '?url',
  //     import: 'default',
  //   })

  //   imgs.value = Object.keys(imageModules).map((path) => ({
  //     src: imageModules[path] as string,
  //     alt: path.split('/').pop() || 'image',
  //   }))
  //   isLoading.value = false
  //   return
  // }

  // In production, wait for secret and decrypt images
  if (!props.secret) {
    isLoading.value = true
    return
  }

  isLoading.value = true
  imgs.value = []

  // Load all images in parallel
  const loadPromises = encryptedImageFiles.map(async (file) => {
    try {
      const response = await fetch(`/imgs/encrypted/${file}`)
      if (!response.ok) {
        console.warn(`Image file not found: ${file}`)
        return null
      }
      const encryptedText = await response.text()
      const imgUrl = await decryptImage(encryptedText, props.secret)
      return {
        src: imgUrl,
        alt: file.replace('.enc', ''),
      }
    } catch (error) {
      console.warn(`Failed to decrypt image ${file}:`, error)
      return null
    }
  })

  // Process images as they complete
  for (const promise of loadPromises) {
    const result = await promise
    if (result) {
      imgs.value.push(result)
      // Show first image immediately
      if (imgs.value.length === 1) {
        isLoading.value = false
      }
    }
  }

  isLoading.value = false
}

// Watch for changes in the secret to reload images
watch(() => props.secret, loadImages, { immediate: true })


</script>

<template>
  <div id="carousel-container">
    <div id="img-overlay">
      <!-- <div class="text-fancy">
        {{ wsyText }}
      </div> -->
    </div>
    <div v-if="isLoading" class="loading-container">
      <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
    </div>
    <Carousel v-else :value="imgs" :numVisible="1" style="width: 100%; height: 100%" :circular="true"
      :autoplayInterval="6000" :showIndicators="false" :showNavigators="false">
      <template #item="slotProps">
        <div class="carousel-item" style="width: 100%; height: 100%">
          <img :src="slotProps.data.src" :alt="slotProps.data.alt" />
        </div>
      </template>
    </Carousel>
  </div>
</template>

<style scoped lang="scss">
#carousel-container {
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  max-width: $breakpoint-tablet;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
}

@media (min-width: $breakpoint-tablet) {
  #carousel-container {
    // max-height: 65vh;
    min-height: 400px;
    border-radius: 18px 18px 0 0;
  }
}

@media (max-width: $breakpoint-tablet) {
  #carousel-container {
    border-radius: 0;
    min-height: 250px;
    // max-height: 50vh;
  }
}

#carousel-container :deep(.p-carousel-content-container) {
  overflow: hidden !important;
}

#img-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  opacity: 0.15;
  pointer-events: inherit;
}

.carousel-item {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
}

.carousel-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center bottom;
}

.text-fancy {
  font-size: 4rem;
}

.loading-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $color-primary;
}
</style>
